import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import styles from "./DeviceTransmissionTable.module.css";
import { useDeviceHealthContextData } from "./context/Context";
import { getVehicleTransmissionData } from "./DeviceHealthService";

export const EXPECTED_PACKETS_PER_MINUTE = 6; // one packet every 10 sec
export const EXPECTED_PACKETS_PER_HOUR = EXPECTED_PACKETS_PER_MINUTE * 60;

const pad = (n) => String(n).padStart(2, "0");

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

// "00" -> "12 AM", "01" -> "1 AM", "12" -> "12 PM", "13" -> "1 PM"
export const formatHour = (hour) => {
    const h = Number(hour);
    return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "AM" : "PM"}`;
};

// shared with the hourly table so both views use the same colour rules:
// below expected -> red, exactly expected -> green, above expected -> yellow
export const getPacketTone = (packets, expected) => {
    if (packets === null || packets === undefined || packets === "") return "none";
    const count = Number(packets);
    if (count < expected) return "bad";
    if (count > expected) return "over";
    return "good";
};

// green status dot when frequency is above 80%, red otherwise
export const isHealthy = (frequency) => Number(frequency) > 80;

export function StatusBadge({ frequency }) {
    return (
        <span className={`${styles.statusBadge} ${isHealthy(frequency) ? styles.statusHealthy : styles.statusUnhealthy}`}>
        </span>
    );
}

const TONE_CLASS = {
    good: styles.tone_green,
    over: styles.tone_yellow,
    bad: styles.tone_red,
    none: styles.tone_grey
};

/*
 * One extra row under the VIN, using the same columns as the table:
 * Depot..Avg Delay columns show the selected hour's values,
 * the hour columns show that hour's minutes (paged, same size as the hour page).
 */
export default function VinMinuteDetail({ selection, minutesPerPage, onClose }) {
    const { vin, hour } = selection;
    const totalPages = Math.ceil(60 / minutesPerPage);
    const [minutePage, setMinutePage] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [minuteRow, setMinuteRow] = useState(null);
    const { GlobalFilter } = useDeviceHealthContextData();

    // same API as the hourly table; sending `hour` returns minute-wise keys ("HH:00".."HH:59")
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        getVehicleTransmissionData({ ...GlobalFilter, search: [vin], hour })
            .then((res) => {
                if (cancelled) return;
                const rows = res?.data || [];
                setMinuteRow(rows.find((r) => r.vin === vin) || null);
            })
            .catch(() => {
                if (!cancelled) setMinuteRow(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [GlobalFilter, vin, hour]);

    const minutes = useMemo(
        () =>
            minuteRow
                ? Array.from({ length: 60 }, (_, minute) => ({
                    minute,
                    packets: minuteRow[`${hour}:${pad(minute)}`]
                }))
                : [],
        [minuteRow]
    );

    const visibleMinutes = minutes.slice(
        minutePage * minutesPerPage,
        (minutePage + 1) * minutesPerPage
    );

    const canGoPrev = minutePage > 0;
    const canGoNext = minutePage < totalPages - 1;

    return (
        <tr className={styles.minuteRow}>
            <td className={styles.minuteRowLabelCell}>
                <span className={styles.minuteRowLabel}>
                    <button
                        type="button"
                        className={styles.minuteCloseBtn}
                        title="Close minute-wise view"
                        onClick={onClose}
                    >
                        <X size={11} />
                    </button>
                    {formatHour(hour)}
                </span>
            </td>
            <td className={styles.minuteRowRange}>Minute-wise</td>
            <td />{/* Device ID column */}
            <td>{minuteRow && <StatusBadge frequency={minuteRow.frequency} />}</td>
            <td>{displayValue(minuteRow?.frequency)}</td>
            <td>{displayValue(minuteRow?.avg_delay)}</td>

            {(isLoading || !minuteRow) && (
                <td colSpan={minutesPerPage}>
                    <div className={styles.loadingState}>
                        {isLoading && <span className={styles.spinner} />}
                        <span>{isLoading ? "Loading..." : "No minute-wise data found"}</span>
                    </div>
                </td>
            )}

            {!isLoading && visibleMinutes.map((m, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === visibleMinutes.length - 1;
                const tone = getPacketTone(m.packets, EXPECTED_PACKETS_PER_MINUTE);

                return (
                    <td
                        key={m.minute}
                        title={`${formatHour(hour).replace(" ", `:${pad(m.minute)} `)} · ${displayValue(m.packets)}/${EXPECTED_PACKETS_PER_MINUTE} packets`}
                        className={`${styles.minuteValueCell} ${styles.hourCellAnimation}`}
                    >
                        <span className={styles.minuteCellContent}>
                            {isFirst && (
                                <button
                                    type="button"
                                    className={styles.minuteNavBtn}
                                    disabled={!canGoPrev}
                                    title="Previous minutes"
                                    onClick={() => setMinutePage((p) => Math.max(p - 1, 0))}
                                >
                                    «
                                </button>
                            )}
                            <span className={styles.minuteCellStack}>
                                <span className={styles.minuteCellTime}>:{pad(m.minute)}</span>
                                <span className={`${styles.minuteCellValue} ${TONE_CLASS[tone]}`}>
                                    {displayValue(m.packets)}
                                </span>
                            </span>
                            {isLast && (
                                <button
                                    type="button"
                                    className={styles.minuteNavBtn}
                                    disabled={!canGoNext}
                                    title="Next minutes"
                                    onClick={() => setMinutePage((p) => Math.min(p + 1, totalPages - 1))}
                                >
                                    »
                                </button>
                            )}
                        </span>
                    </td>
                );
            })}
        </tr>
    );
}
